import { Modal } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

/**
 * @param {(params: Object) => Promise<{ invoices: any[], cursor?: string, count?: number, amount?: number }>} fetchFn
 * @param {Object|null} params
 * @returns {{
 *   loading: boolean,
 *   items: any[],
 *   currentPage: number,
 *   hasNextPage: boolean,
 *   onNextPage: () => void,
 *   onPrevPage: () => void,
 *   count: number | undefined,
 *   amount: number | undefined,
 *   error: Error | null,
 * }}
 */
export default function useReportPagination(fetchFn, params) {
  const { t } = useTranslation()
  const [loading, setLoading] = React.useState(false)
  const [items, setItems] = React.useState([])
  const [currentPage, setCurrentPage] = React.useState(1)
  const [nextCursor, setNextCursor] = React.useState(undefined)
  const [pageCache, setPageCache] = React.useState({})
  const [count, setCount] = React.useState(undefined)
  const [amount, setAmount] = React.useState(undefined)
  const [error, setError] = React.useState(null)

  const fetchFnRef = React.useRef(fetchFn)
  fetchFnRef.current = fetchFn

  const loadPage = React.useCallback(
    async (targetPage, pageCursor) => {
      setLoading(true)
      setError(null)
      try {
        const res = await fetchFnRef.current({ ...params, cursor: pageCursor })
        const list = Array.isArray(res) ? res : res?.invoices ?? []
        setItems(list)
        setNextCursor(res?.cursor)
        setCount(res?.count)
        setAmount(res?.amount)
        setPageCache((prev) => ({
          ...prev,
          [targetPage]: { items: list, nextCursor: res?.cursor, cursor: pageCursor },
        }))
      } catch (err) {
        setError(err)
        Modal.error({
          title: t('app.common.error'),
          content: t('app.common.submitError'),
        })
      }
      setLoading(false)
    },
    [params, t],
  )

  React.useEffect(() => {
    if (!params) return
    setCurrentPage(1)
    setNextCursor(undefined)
    setPageCache({})
    setCount(undefined)
    setAmount(undefined)
    setError(null)
    loadPage(1, undefined)
  }, [loadPage, params])

  const onNextPage = () => {
    const targetPage = currentPage + 1
    const cached = pageCache[targetPage]
    if (cached) {
      setItems(cached.items)
      setNextCursor(cached.nextCursor)
      setCurrentPage(targetPage)
      return
    }
    if (!nextCursor) return
    loadPage(targetPage, nextCursor)
    setCurrentPage(targetPage)
  }

  const onPrevPage = () => {
    if (currentPage > 1) {
      const targetPage = currentPage - 1
      const cached = pageCache[targetPage]
      if (cached) {
        setItems(cached.items)
        setNextCursor(cached.nextCursor)
      }
      setCurrentPage(targetPage)
    }
  }

  const hasNextPage = Boolean(nextCursor || pageCache[currentPage + 1])

  return { loading, items, currentPage, hasNextPage, onNextPage, onPrevPage, count, amount, error }
}
